// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Estoque/InventarioForm.tsx
//  Criar inventário + lançar contagem + processar
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, CheckCircle2, ClipboardList } from 'lucide-react';
import { useInventario, useInventarioDetalhe } from '@/features/estoque/hooks/useInventario';
import type { ContagemItemRequest } from '@/features/estoque/types';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

// ── NovoInventarioForm ────────────────────────────────────────────────────

function NovoInventarioForm() {
  const navigate = useNavigate();
  const hoje = new Date().toISOString().slice(0, 10);

  const [dtMovimento,  setDtMovimento]  = useState(hoje);
  const [numDocumento, setNumDocumento] = useState('');

  const { create, isCreating } = useInventario();

  const handleSubmit = async () => {
    const inv = await create({ dtMovimento, numDocumento: numDocumento || undefined });
    navigate(`/estoque/inventario/${inv.id}`);
  };

  return (
    <div className="p-6 space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/estoque/inventario')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Novo Inventário</h1>
          <p className="text-muted-foreground">Registre os dados do inventário para iniciar a contagem</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Dados do Inventário</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Data do Inventário *</Label>
            <Input type="date" value={dtMovimento} onChange={(e) => setDtMovimento(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Nº Documento</Label>
            <Input placeholder="Opcional" value={numDocumento} onChange={(e) => setNumDocumento(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/estoque/inventario')}>Cancelar</Button>
        <Button className="gap-2" onClick={handleSubmit} disabled={isCreating}>
          <Save className="h-4 w-4" />
          {isCreating ? 'Criando...' : 'Criar Inventário'}
        </Button>
      </div>
    </div>
  );
}

// ── InventarioDetalheForm ─────────────────────────────────────────────────

type ItemRow = ContagemItemRequest & { _key: number };

function InventarioDetalheForm({ id }: { id: number }) {
  const navigate = useNavigate();

  const { data: inv, isLoading } = useInventarioDetalhe(id);
  const { lancarContagem, processar, isProcessando } = useInventario();

  const [itens,   setItens]   = useState<ItemRow[]>([]);
  const [nextKey, setNextKey] = useState(1);

  const addItem = () =>
    setNextKey((k) => { setItens((p) => [...p, { _key: k, subProdutoId: 0, quantidade: 0 }]); return k + 1; });

  const removeItem = (key: number) => setItens((p) => p.filter((r) => r._key !== key));

  const updateQtd = (key: number, quantidade: number) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, quantidade } : r));

  const setItemProduto = (key: number, subProdutoId: number) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, subProdutoId } : r));

  const handleLancar = async () => {
    if (itens.length === 0 || itens.some((i) => i.subProdutoId === 0)) return;
    await lancarContagem({ id, req: { itens: itens.map(({ _key: _k, ...rest }) => rest) } });
    setItens([]);
  };

  const handleProcessar = async () => {
    await processar(id);
    navigate('/estoque/inventario');
  };

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando...</div>;
  if (!inv)      return <div className="p-6 text-muted-foreground">Inventário não encontrado.</div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/estoque/inventario')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">Inventário {inv.numDocumento ? `nº ${inv.numDocumento}` : `de ${new Date(inv.dtMovimento).toLocaleDateString('pt-BR')}`}</h1>
            {inv.processado
              ? <Badge className="bg-green-500/10 text-green-600">Processado</Badge>
              : <Badge className="bg-blue-500/10 text-blue-600">Em Aberto</Badge>}
          </div>
          <p className="text-muted-foreground">
            {inv.numDocumento && `Doc: ${inv.numDocumento} · `}
            Data: {new Date(inv.dtMovimento).toLocaleDateString('pt-BR')}
          </p>
        </div>
      </div>

      {/* Itens já lançados */}
      {inv.itens.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />Contagens Lançadas ({inv.itens.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Saldo Sistema</TableHead>
                  <TableHead className="text-right">Qtd. Contada</TableHead>
                  <TableHead className="text-right">Divergência</TableHead>
                  <TableHead>Data Lançamento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inv.itens.map((item) => {
                  const div = item.divergencia ?? 0;
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.produtoDescricao}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {(item.saldoSistema ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {item.quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${div > 0 ? 'text-green-600' : div < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {div > 0 ? '+' : ''}{div.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>{new Date(item.dtMovimento).toLocaleString('pt-BR')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Novo lançamento de contagem */}
      {!inv.processado && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Lançar Contagem</CardTitle>
              <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
                <Plus className="h-4 w-4" />Adicionar Item
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {itens.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground">Clique em "Adicionar Item" para lançar a contagem.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Produto</TableHead>
                      <TableHead className="text-right">Quantidade Contada</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itens.map((item) => (
                      <TableRow key={item._key}>
                        <TableCell>
                          <ProdutoSearchCombobox
                            value={item.subProdutoId || undefined}
                            onChange={(id) => setItemProduto(item._key, id)}
                            placeholder="Buscar produto..."
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min={0}
                            step={0.001}
                            className="text-right w-32"
                            value={item.quantidade}
                            onChange={(e) => updateQtd(item._key, Number(e.target.value))}
                          />
                        </TableCell>
                        <TableCell>
                          <Button size="icon" variant="ghost" onClick={() => removeItem(item._key)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {itens.length > 0 && (
              <div className="flex justify-end">
                <Button
                  className="gap-2"
                  onClick={handleLancar}
                  disabled={itens.some((i) => i.subProdutoId === 0)}
                >
                  <Save className="h-4 w-4" />Salvar Contagem
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Processar / Fechar inventário */}
      {!inv.processado && (
        <>
          <Separator />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate('/estoque/inventario')}>Voltar</Button>
            <Button
              className="gap-2 bg-green-600 hover:bg-green-700"
              onClick={handleProcessar}
              disabled={isProcessando || inv.itens.length === 0}
            >
              <CheckCircle2 className="h-4 w-4" />
              {isProcessando ? 'Processando...' : 'Processar Inventário'}
            </Button>
          </div>
        </>
      )}

      {inv.processado && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => navigate('/estoque/inventario')}>Voltar</Button>
        </div>
      )}
    </div>
  );
}

// ── Roteamento interno ────────────────────────────────────────────────────

const InventarioForm = () => {
  const { id } = useParams<{ id: string }>();
  const numId = id ? Number(id) : 0;
  if (numId > 0) return <InventarioDetalheForm id={numId} />;
  return <NovoInventarioForm />;
};

export default InventarioForm;
